import { useCallback } from "react";
import { useSetAtom } from "jotai";
import { useApi } from "./useApi";
import { useSessionSetAtom } from "../useSessionSetAtom";
import type { UserResponse } from "@/models/user";
import {
    currentUserAtom,
    isFetchingUserAtom,
    isProcessingUserAtom,
    userFetchErrorAtom,
} from "@/store/userAtoms";

// Email is deliberately absent: it is the CIAM sign-in identifier, managed in
// Entra, and UpdateMeUserCommandBody on the API does not accept it.
export interface UpdateMeUserRequest {
    firstName: string;
    lastName: string;
    phoneNumber: string;
}

export const useUserApi = () => {
    const { callApi } = useApi();
    // Session-bound: a session that ended while a read was on the wire has
    // already been cleared (endSessionAtom); its late answer must not refill it.
    const setCurrentUser = useSessionSetAtom(currentUserAtom);
    const setIsFetchingUser = useSetAtom(isFetchingUserAtom);
    const setIsProcessingUser = useSetAtom(isProcessingUserAtom);
    const setFetchError = useSessionSetAtom(userFetchErrorAtom);

    const getMeUser = useCallback(async (): Promise<UserResponse> => {
        try {
            setIsFetchingUser(true);
            setFetchError(false);

            const user = await callApi<UserResponse>("/users/me");
            setCurrentUser(user);

            return user;
        } catch (error) {
            setFetchError(true);
            throw error;
        } finally {
            setIsFetchingUser(false);
        }
    }, [callApi, setCurrentUser, setIsFetchingUser, setFetchError]);

    const updateMeUser = useCallback(
        async (data: UpdateMeUserRequest): Promise<void> => {
            setIsProcessingUser(true);

            try {
                await callApi("/users/me", {
                    method: "PUT",
                    body: JSON.stringify(data),
                });
            } finally {
                setIsProcessingUser(false);
            }
        },
        [callApi, setIsProcessingUser],
    );

    return {
        getMeUser,
        updateMeUser,
    };
};
